import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowLeft,
  ClipboardList,
  Clock,
  ShieldCheck,
  Play,
} from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';

export const Route = createFileRoute('/_authed/candidate/aptitude')({
  component: AptitudeLauncherPage,
});

function AptitudeLauncherPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const startMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ runId: string; templateId: string }>(
        '/api/me/hiring/aptitude/start',
        { method: 'POST', body: {} },
      ),
    onSuccess: (data) => {
      void navigate({
        to: '/assessments/runs/$runId/start',
        params: { runId: data.runId },
      });
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError
          ? err.message
          : 'Could not start the aptitude test.',
        { variant: 'error' },
      ),
  });

  return (
    <ListShell
      title="Aptitude test"
      description="A short, timed test to help us understand your fit. Take it when you're undisturbed."
      primaryAction={
        <Link
          to="/candidate/status"
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Back to my application
        </Link>
      }
    >
      <section className="mx-auto max-w-2xl space-y-6">
        <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-6 shadow-sm">
          <header className="flex items-center gap-3">
            <span className="rounded-full bg-gold-50 p-2 text-gold-700 dark:bg-gold-700/20 dark:text-gold-50">
              <ClipboardList size={20} aria-hidden="true" />
            </span>
            <h2 className="font-display text-xl font-semibold">
              Before you begin
            </h2>
          </header>
          <ul className="mt-4 space-y-3 text-sm text-[color:var(--color-text)]">
            <li className="flex items-start gap-2">
              <Clock
                size={16}
                className="mt-0.5 shrink-0 text-[color:var(--color-text-muted)]"
                aria-hidden="true"
              />
              <span>
                The test is <strong>timed</strong>. The timer starts the
                moment you click Begin and cannot be paused.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck
                size={16}
                className="mt-0.5 shrink-0 text-[color:var(--color-text-muted)]"
                aria-hidden="true"
              />
              <span>
                You may attempt this test <strong>once</strong>. Reloading the
                tab will not reset your attempt.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ClipboardList
                size={16}
                className="mt-0.5 shrink-0 text-[color:var(--color-text-muted)]"
                aria-hidden="true"
              />
              <span>
                Answers save as you go. When the timer runs out the test
                auto-submits.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck
                size={16}
                className="mt-0.5 shrink-0 text-[color:var(--color-text-muted)]"
                aria-hidden="true"
              />
              <span>
                Please do not use outside help. We verify with you during the
                interview — integrity here matters.
              </span>
            </li>
          </ul>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <Link
            to="/candidate/status"
            className="text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
          >
            Cancel
          </Link>
          <Button
            size="lg"
            icon={<Play size={18} aria-hidden="true" />}
            loading={startMutation.isPending}
            onClick={() => startMutation.mutate()}
          >
            Begin aptitude test
          </Button>
        </div>
      </section>
    </ListShell>
  );
}
